const VISITOR_KEY = 'filmsomniac.analytics.visitorId'
const SESSION_KEY = 'filmsomniac.analytics.session'
const SESSION_TIMEOUT_MS = 30 * 60 * 1000
const ENDPOINT = '/api/v1/analytics/pageview/'

function storage() {
  if (typeof window !== 'undefined' && window.localStorage) {
    return window.localStorage
  }
  if (typeof globalThis !== 'undefined' && globalThis.localStorage) {
    return globalThis.localStorage
  }
  return null
}

function createId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (char) => {
    const random = crypto.getRandomValues(new Uint8Array(1))[0]
    return (Number(char) ^ (random & (15 >> (Number(char) / 4)))).toString(16)
  })
}

function readSession(ls, now) {
  try {
    const rawSession = ls.getItem(SESSION_KEY)
    const parsed = rawSession ? JSON.parse(rawSession) : null
    if (
      parsed?.sessionId &&
      Number.isFinite(parsed.lastSeen) &&
      now - parsed.lastSeen <= SESSION_TIMEOUT_MS
    ) {
      return parsed.sessionId
    }
  } catch {
    return createId()
  }
  return createId()
}

export function analyticsIds(now = Date.now()) {
  const ls = storage()
  if (!ls) {
    return {
      visitorId: createId(),
      sessionId: createId(),
    }
  }

  let visitorId = ls.getItem(VISITOR_KEY)
  if (!visitorId) {
    visitorId = createId()
    ls.setItem(VISITOR_KEY, visitorId)
  }

  const sessionId = readSession(ls, now)
  ls.setItem(SESSION_KEY, JSON.stringify({ sessionId, lastSeen: now }))

  return { visitorId, sessionId }
}

function fullUrlFor(route) {
  const fullPath = route?.fullPath || route?.path || '/'
  if (/^https?:\/\//i.test(fullPath)) {
    return fullPath
  }
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  return `${origin}${fullPath}`
}

function shouldTrack(route) {
  const path = route?.path || ''
  return !(
    path.startsWith('/admin') ||
    path.startsWith('/badmin') ||
    path.startsWith('/api')
  )
}

function pageViewPayload(to, from) {
  const { visitorId, sessionId } = analyticsIds()
  return {
    visitor_id: visitorId,
    session_id: sessionId,
    path: to?.path || '/',
    full_url: fullUrlFor(to),
    route_name: to?.name || '',
    route_params: to?.params || {},
    previous_path: from?.fullPath || '',
    referrer_url: typeof document !== 'undefined' ? document.referrer || '' : '',
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent || '' : '',
    viewport_width: typeof window !== 'undefined' ? window.innerWidth || null : null,
  }
}

function sendAnonymous(body) {
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' })
    if (navigator.sendBeacon(ENDPOINT, blob)) {
      return true
    }
  }
  if (typeof fetch === 'undefined') {
    return false
  }
  return fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {})
}

export function trackPageView(to, from = {}, auth = null) {
  if (!shouldTrack(to)) {
    return false
  }

  try {
    const body = JSON.stringify(pageViewPayload(to, from))
    const access = auth?.state?.access || null
    if (!access || typeof fetch === 'undefined') {
      return sendAnonymous(body)
    }
    return fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${access}`,
      },
      body,
      keepalive: true,
    }).catch(() => {})
  } catch {
    return false
  }
}
